import { intersection } from '@direct-frontend/stdlib';
import { tryParseJSON } from '@adv-frontend/yastore-modules/lib';
import { type OpenApiSchemaSetting, type OpenApiSchemaSettingHierarchy } from '#shared/api/schema';
import { useGetSettings } from '../../api';
import { SETTINGS_LEAVES_NON_PRIMITIVES_ARRAY } from '../../constants';
import { type SettingsLeafPaths, type SettingsPrimitive } from '../../types';
import { getSettingPatchFunction } from '../getSettingPatchFunction';

const PRIMITIVE_SETTINGS = ['integer', 'multiline', 'string', 'secret', 'boolean', 'string_array'];

const getPrimitiveSetting = <Setting extends SettingsPrimitive>(
    setting: OpenApiSchemaSetting,
    filters: Array<(setting: OpenApiSchemaSetting) => boolean>
): SettingsPrimitive | undefined => {
    //  Проверяем на наличие типа и его соответствие примитивным типам
    if (!setting.type) {
        return undefined;
    }

    //  Проверяем что настройка проходит все дополнительные фильтры
    if (!filters.every((filter) => filter(setting))) {
        return undefined;
    }

    return {
        value: tryParseJSON(setting.value),
        patch: getSettingPatchFunction(setting.path as SettingsLeafPaths),
        type: setting.type,
        title: setting.label || '',
        path: setting.path,
    } as Setting;
};

interface UseSettingsPrimitiveProps {
    paths?: Array<string>;
    skip?: boolean;
    tags?: Array<string>;
}

export function useSettingsPrimitive({ paths = [], skip = false, tags = [] }: UseSettingsPrimitiveProps = {}) {
    const { data, status } = useGetSettings({}, { skip });

    const primitives: Array<SettingsPrimitive> = [];
    const nonPrimitives: Array<SettingsPrimitive> = [];

    //  Проверяем соответствие мета данных текущей настройки массиву tags
    const checkTags = (setting: OpenApiSchemaSetting) =>
        tags.length ? intersection(setting.meta, tags).length > 0 : true;

    //  Если в массиве путей(paths) есть значения, то проверяем что текущая настройка принадлежит хотя бы одному из путей
    //  Если в массиве путей(paths) нет значений, то идем дальше
    const checkPaths = (setting: OpenApiSchemaSetting) =>
        paths.length ? paths.some((path) => setting.path === path) : true;

    const parseSettings = (
        currentSettings: OpenApiSchemaSettingHierarchy,
        allSettings: OpenApiSchemaSettingHierarchy
    ) => {
        const setting = getPrimitiveSetting(currentSettings.setting, [checkTags, checkPaths]);

        if (setting) {
            if (
                PRIMITIVE_SETTINGS.includes(setting.type) &&
                !SETTINGS_LEAVES_NON_PRIMITIVES_ARRAY.includes(
                    setting.path as (typeof SETTINGS_LEAVES_NON_PRIMITIVES_ARRAY)[number]
                )
            ) {
                primitives.push(setting);
            } else {
                nonPrimitives.push(setting);
            }
        }

        //  Рекурсивно парсим всех детей текущего узла настроек
        currentSettings.children.forEach((child) => {
            parseSettings(child, allSettings);
        });
    };

    if (data) {
        data.children.forEach((child) => parseSettings(child, data));
    }

    return { primitives, nonPrimitives, status };
}
